import { NgForm } from '@angular/forms';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FinancialTypes, FinancialAreas } from '@xbs/xbs-enums';

import {
  ClosableModalWithErrorResponseModel
} from '../../../../shared/components/base/closable-modal-with-error-response-model';
import {
  NotificationService,
  UtilsService,
  NumberFormatService,
  FinancialDataService,
  SessionService,
  EntitiesService,
} from '../../../../shared/services';
import { ModalService } from '../../../../shared/modules/modal';
import {
  FinancialDataValueEnteredInEnum,
  FinancialData,
  FinancialDataErrorResponse,
  FinancialDataPayload,
  FinancialDataModalPayload,
  FinancialDataObjectTypeEnum,
  Country,
  FinancialArea,
  Entity,
} from '../../../../shared/models';
import { FinancialSectionConstants } from 'src/app/shared/constants';

@Component({
  selector: 'financials-data-modal',
  templateUrl: './financials-data-modal.component.html',
})
export class FinancialsDataModalComponent
  extends ClosableModalWithErrorResponseModel<FinancialDataErrorResponse> implements OnInit {
  @ViewChild('form') form: NgForm;

  data: FinancialDataModalPayload;
  section: string;
  shouldGetEntity: boolean;
  taxYear: number;
  financialArea: FinancialArea;
  typesWithData: FinancialData[] = [];
  financialData = {};
  inputCurrencyOptions = [];
  currencyInput: number;
  inputCurrencyDisabled = false;
  inputsOldValue = {
    0: '',
    1: '',
    2: '',
    3: '',
    4: '',
    5: '',
    6: '',
    7: '',
    8: '',
    9: '',
  };
  minFractionDigits = 2;
  mneCurrency = '';
  entityCurrency = '';
  countries: Country[] = [];
  entity: Entity;
  modalWarningMessage = '';

  constructor(
    private entitiesService: EntitiesService,
    protected notificationService: NotificationService,
    protected modalService: ModalService,
    private _utilsService: UtilsService,
    private _numberFormatService: NumberFormatService,
    private _financialDataService: FinancialDataService,
    private _sessionService: SessionService
  ) {
    super(notificationService, modalService);
    this.validationErrors = new FinancialDataErrorResponse();
  }

  ngOnInit(): void {
    this.listenForErrorMessages();
    this.prepareData();
    this.getCountries();
    if (this.shouldGetEntity) {
      this.getEntity();
    }
    this.inputCurrencyDisabled = !!this.currencyInput;
    this.mneCurrency = this._sessionService.getContainerUpeCurrency().isoCode;
    this.getInputCurrencyOptions();
  }

  private getEntity(): void {
    this.entitiesService.show(this.data.objectId).subscribe(({ data: entityData }): void => {
      this.entity = entityData;
      this.modalWarningMessage = `financial_area.${this.section}_modal_warning_message`;
    });
  }

  save(): void {
    if (!this.currencyInput) {
      // mark form touched to display error messages
      this.markFormTouched();
      return;
    }

    if (this.isSaveDisabled) {
      return;
    }

    this.isSaveDisabled = true;
    this.responseErrors = [];
    const financialDataPayload = new FinancialDataPayload();

    financialDataPayload.valueEnteredIn = this.currencyInput;
    financialDataPayload.financialData = this.getNonEmptyFinancialDataValues();

    this._financialDataService
      .saveFinancialData(
        this.data.objectId,
        this.data.financialArea.financialAreaId,
        this.data.objectType,
        financialDataPayload
      )
      .subscribe(() => {
        this.success();
        this.closeAllModals();
      });
  }

  getFormated(num, maxFractionDigits = 1): string {
    if (num === '.') {
      return '0.';
    }

    return this._numberFormatService.getFormatedNumber(num, maxFractionDigits, 2);
  }

  formatNumberInput(event, element, field, maxFractionDigits = 2, data): void {
    this.markFormTouched(true);

    const validInputRegex = /^[-]?[0-9,]*[.]?[.]?[0-9]*$/g;
    const firstChar = event.target.value.slice(0, 1);
    const startsWithDotOrComma = firstChar.includes('.') || firstChar.includes(',') && event.target.value !== '.00';
    const startsWithMinusDot = event.target.value.slice(0, 2) === '-.';

    if (!validInputRegex.test(event.target.value)) {
      event.target.value = this.inputsOldValue[field];

      return;
    }

    data.value = this._numberFormatService.handleCursorPosition(
      event,
      element,
      data.value,
      maxFractionDigits,
      startsWithDotOrComma,
      startsWithMinusDot,
      this.minFractionDigits
    );
  }

  getInputCurrencyValueName(): string {
    const value = this.inputCurrencyOptions.find(option => option.id === this.currencyInput);

    return `${value.name} (${this.getCurrencyIsoCode(value.id)})`;
  }

  getCurrencyIsoCode(optionId: number): string {
    let currency = this.entityCurrency;
    if (optionId === FinancialDataValueEnteredInEnum.MNE) {
      currency = this.mneCurrency;
    }

    return `${currency}`;
  }

  setOldValue(event, field): void {
    this.inputsOldValue[field] = event.target.value;
  }

  getPrimaryHeaderLabelTranslationKey(): string {
    switch (this.data.objectType) {
      case FinancialDataObjectTypeEnum.ENTITY:
        return 'global.entity';
      case FinancialDataObjectTypeEnum.MNE:
        return 'global.mne';
      case FinancialDataObjectTypeEnum.TESTED_PARTY:
        return 'global.analysis_name';
      default:
        return '';
    }
  }

  getSecondaryHeaderLabelTranslationKey(): string {
    switch (this.data.objectType) {
      case FinancialDataObjectTypeEnum.ENTITY:
        return 'legal_entity.entity_full_name';
      case FinancialDataObjectTypeEnum.TESTED_PARTY:
        return 'tested_party.tested_party_name';
      default:
        return '';
    }
  }

  getInstructionsTranslationKey(): string {
    const area = this.data.financialArea.name.split('.')[1];
    return `financial_area.provide_${area}`;
  }

  getTitleTranslationKey(): string {
    const area = this.data.financialArea.name.split('.')[1];
    const type = this.getObjectTypeKey();

    return `financial_area.financial_data_modal_title_${area}_${type}`;
  }

  isGrossProfitOrOperatingProfit(financialType: FinancialData): boolean {
    const { financialTypeId } = financialType.financialType;
    const grossProfitId = FinancialTypes.ByName.GrossProfit.Id;
    const operatingProfitId = FinancialTypes.ByName.OperatingProfit.Id;

    return financialTypeId === grossProfitId || financialTypeId === operatingProfitId;
  }

  setGrossProfitAndOperatingProfit(): void {
    if (this.financialArea.financialAreaId === FinancialAreas.ByName.IncomeStatement.Id) {
      this.financialData[FinancialTypes.ByName.GrossProfit.Id].value = this.calculateGrossProfit();
      this.financialData[FinancialTypes.ByName.OperatingProfit.Id].value = this.calculateOperatingProfit();
    }
  }

  private getInputCurrencyOptions(): void {
    !this.entityCurrency || this.entityCurrency === this.mneCurrency ?
      this.inputCurrencyOptions.push({ id: FinancialDataValueEnteredInEnum.MNE, name: 'MNE' }) :
      this.inputCurrencyOptions = this._utilsService.enumToArray(FinancialDataValueEnteredInEnum);
  }

  private setEntityCurrency(): void {
    const entityCountry = this.countries.find(country => country.countryId === this.data.entityCountryId);
    this.entityCurrency = entityCountry && entityCountry.defaultCurrency && entityCountry.defaultCurrency.isoCode ? entityCountry.defaultCurrency.isoCode : '';
  }

  private getCountries(): void {
    this.countries = this._sessionService.getCountries();
    this.setEntityCurrency();
  }

  private markFormTouched(onlySelf = false) {
    if (onlySelf) {
      this.form.form.markAsTouched();
      return;
    }

    Object.values(this.form.form.controls).forEach(control => {
      control.markAsTouched();
      control.updateValueAndValidity();
    });
  }

  private getObjectTypeKey(): string {
    switch (this.data.objectType) {
      case FinancialDataObjectTypeEnum.ENTITY:
        return 'entity';
      case FinancialDataObjectTypeEnum.MNE:
        return 'mne';
      case FinancialDataObjectTypeEnum.TESTED_PARTY:
        return 'tested_party';
      default:
        return '';
    }
  }

  private getValueEnteredIn(): number {
    const data = this.typesWithData.find(typeWithData => Boolean(typeWithData.valueEnteredIn));
    if (data) {
      return data.valueEnteredIn;
    }

    return null;
  }

  private prepareData(): void {
    this.financialArea = this.data.financialArea;
    this.typesWithData = JSON.parse(JSON.stringify(this.data.typesWithData));
    this.setCurrencyInput();
    this.taxYear = this.data.taxYear;
    this.cacheFinancialDataLocations();
    this.setGrossProfitAndOperatingProfit();
    this.section = this.data.section || '';
    const pbaSections = [FinancialSectionConstants.PBA_INCOME_STATEMENT, FinancialSectionConstants.PBA_BALANCE_SHEET];
    this.shouldGetEntity = !pbaSections.includes(this.data.section);

    if (this.currencyInput === FinancialDataValueEnteredInEnum.Entity) {
      const currentYearCurrencyConversion = this.data.objectCurrencyConversion.find(objectCurrency => objectCurrency.taxYear === this.taxYear);
      const localScale = +currentYearCurrencyConversion.localCurrencyScale;
      const conversion = +currentYearCurrencyConversion.conversionValue;
      const mneCurrencyScale = this._sessionService.getContainerUpeCurrencyScale();

      this.typesWithData.map((data) => {
        if (!data.value) {
          return data;
        }

        data.value = this._utilsService.calcToLocalCurrency(data.value, mneCurrencyScale, conversion, localScale);

        return data;
      });
    }
  }

  private cacheFinancialDataLocations(): void {
    if (this.financialArea.financialAreaId === FinancialAreas.ByName.IncomeStatement.Id) {
      this.typesWithData.forEach((type) => {
        this.financialData[type.financialType.financialTypeId] = type;
      });
    }
  }

  private calculateGrossProfit(): number {
    let totalNetSales = this.financialData[FinancialTypes.ByName.TotalNetSales.Id].value || null;
    let costOfGoodsSold = this.financialData[FinancialTypes.ByName.CostOfGoodsSold.Id].value || null;

    if (totalNetSales === null || costOfGoodsSold === null) {
      return null;
    } else {
      return Number(totalNetSales) - Number(costOfGoodsSold);
    }
  }

  private calculateOperatingProfit(): number {
    let grossProfit = this.financialData[FinancialTypes.ByName.GrossProfit.Id].value;
    let operatingExpenses = this.financialData[FinancialTypes.ByName.OperatingExpenses.Id].value || null;

    if (grossProfit === null || operatingExpenses === null) {
      return null;
    } else {
      return Number(grossProfit) - Number(operatingExpenses);
    }
  }

  private setCurrencyInput(): void {
    if (this.data.objectType === FinancialDataObjectTypeEnum.MNE) {
      this.inputCurrencyDisabled = true;
      this.currencyInput = FinancialDataValueEnteredInEnum.MNE;
      return;
    }
    this.currencyInput = this.getValueEnteredIn();
  }

  private getNonEmptyFinancialDataValues(): FinancialData[] {
    return this.typesWithData.filter((data) => {
      if (data.value || data.value === 0 || data.value === null) {
        return true;
      }
    });
  }
}
